import React, { useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// ANT DESIGN IMPORTS
import { ArrowDownOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  Pagination,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";

// PROJECT IMPORTS
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import "../../../assets/styles/InterbankPlacement.css";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../treasury.config";

import DownloadTableButtons from "../../../components/layouts/enhancement/DownloadTableButtons";
import { rightAction } from "../../../redux/actions";

const columns = [
  {
    dataIndex: "productName",
    // sorter: (a, b) => a.productName.localeCompare(b.productName),
    sorter: (a, b) => String(a.id || "").localeCompare(String(b.id || "")),

    title: (column) => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        Product Name
      </div>
    ),
    sortDirections: ["ascend"],
  },
  {
    title: "Counterparty Type",
    dataIndex: "counterpartyType",
    width: "25%",
    render: (counterpartyTypes) => (
      <div>
        {counterpartyTypes.map((type, index) => (
          <Tag
            key={index}
            style={{
              borderRadius: "9999px",
              padding: "2px 12px",
              color: "#9F1AB1",
              backgroundColor: "#FDF4FF",
              marginBottom: "4px",
              border: "1px solid #F6D0FE",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "18px",
            }}
          >
            {type}
          </Tag>
        ))}
      </div>
    ),
  },
  {
    title: "Currency",
    dataIndex: "currency",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (text) => {
      let bgColor = "#F9FAFB"; // default bg
      let textColor = "#344054"; // default text
      let border = "1px solid #EAECF0";

      switch (text) {
        case "Active":
          bgColor = "#ECFDF3";
          textColor = "#067647";
          border = "1px solid #ABEFC6";
          break;
        case "Inactive":
          bgColor = "#FEE4E2";
          textColor = "#B42318";
          break;
        // case "Disabled":
        //   bgColor = "#EFF8FF";
        //   textColor = "#175CD3";
        //   border = "1px solid #B2DDFF";
        //   break;
        case "Closed":
          bgColor = "#FEF3F2";
          textColor = "#B42318";
          border = "1px solid #FECDCA";
          break;
        case "Approved":
          bgColor = "#EEF4FF";
          textColor = "#3538CD";
          border = "1px solid #C7D7FE";
          break;
        case "WithdrawnByApplicant":
          bgColor = "#F0F9FF";
          textColor = "#026AA2";
          border = "1px solid #B9E6FE";
          break;
        case "Pending":
          bgColor = "#F4F3FF";
          textColor = "#5925DC";
          border = "1px solid #D9D6FE";
          break;
        default:
          break;
      }

      return (
        <Tag
          style={{
            padding: "2px 12px",
            borderRadius: "16px",
            border: border,
            fontSize: "12px",
            fontWeight: 500,
            display: "inline-block",
            backgroundColor: bgColor,
            color: textColor,
            textTransform: "capitalize",
          }}
        >
          {text}
        </Tag>
      );
    },
  },
];

const ManageInterBankPlacement = (props) => {
  //DECLARED VARIABLES
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  // const { monthList } = appStatic;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  // const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableCount, setTableCount] = useState("10");
  const [interBankPlacementList, setInterBankList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("Active");
  const pageSize = 10;

  useEffect(() => {
    BOM.HasPermission("READ_INTERBANK_PLACEMENT_PRODUCT") &&
      getInterbankPlacementListAction();
    BOM.HasPermission("CREATE_INTERBANK_PLACEMENT_PRODUCT") &&
      rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return;
    }
    if (type === "next") {
      return;
    }
    return originalElement;
  };

  const filteredData = useMemo(() => {
    if (!selectedStatus || selectedStatus === "Status") {
      return originalList;
    }

    return originalList.filter(
      (item) => item?.status?.toLowerCase() === selectedStatus.toLowerCase()
    );
  }, [selectedStatus, originalList]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // Handle select change
  function handleFilterByStatus(newStatus) {
    setSelectedStatus(newStatus);
  }

  function handleSearchInTable(e) {
    setSearchKey(e.target.value);
  }

  const interBankData = useMemo(() => {
    const filtered = filteredData.filter((item) =>
      [item.productName, item.currency, item.productCode].some((field) =>
        field.toLowerCase().includes(searchKey.toLowerCase())
      )
    );

    const start = (currentPage - 1) * pageSize;
    const end = currentPage * pageSize;
    return filtered.slice(start, end);
  }, [filteredData, searchKey, currentPage]);

  const getInterbankPlacementListAction = () => {
    // START LOAD
    setIsLoading(true);

    let body = { session };

    BOM.FetchReqAction(
      body,
      apiConfig.InterBankPlacement,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        } else {
          const objList = res;
          let tempArray = [];

          objList.forEach((element) => {
            const counterpartyTypeNames =
              element?.counterpartyTypes?.map((type) => type.name) || [];
            tempArray.push({
              id: element?.id,
              key: element?.id,
              productName: element?.productName || "-",
              productCode: element?.productCode || "-",
              counterpartyType: counterpartyTypeNames,
              currency: element?.currencyCode || "-",
              status: element?.status?.text || "-",
            });
          });

          //Display only active records list on the table; this will change after switching from authorized to disabled and vice versa.
          // setInterBankList(tempArray.filter((item) => item.disabled === false));
          setInterBankList(tempArray);

          //keep the orginal data to use after gList is modified when change from autorise to disable
          setOriginalList(tempArray);

          //STOP LOAD
          setIsLoading(false);
        }
      },
      "treasury"
    );
  };

  const getCsvListData = () => {
    return new Promise((resolve, reject) => {
      const params = {
        limit: 1000,
        offset: 0,
      };

      let body = {
        session,
        params,
      };

      BOM.FetchReqAction(body, apiConfig.InterBankPlacement, (err, res) => {
        if (err) {
          reject(err);
        } else {
          const objList = res;
          let tempArray = [];

          objList.forEach((element) => {
            const counterpartyTypeNames =
              element?.counterpartyTypes?.map((type) => type.name) || [];
            tempArray.push({
              id: element?.id,
              key: element?.id,
              productName: element?.productName || "-",
              productCode: element?.productCode || "-",
              counterpartyType: counterpartyTypeNames,
              currency: element?.currencyCode || "-",
              status: element?.status?.text || "-",
            });
          });

          resolve(tempArray);
        }
      });
    }, "treasury");
  };

  //DECLARED METHODS
  const onSelectItemAction = (objParam, selectedRow) => {
    // If no row or multiple rows selected, reset read and update
    // console.log(objParam);
    // console.log(selectedRow);

    if (objParam.length !== 1) {
      setSelectedRow(null);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });

      // Re-activate other buttons that should remain (like addBtn)
      BOM.HasPermission("CREATE_INTERBANK_PLACEMENT_PRODUCT") &&
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }
    // console.log(objParam);

    // Only one row is selected
    setSelectedRow(objParam[0]);
    // [
    //   BOM.HasPermission("READ_LOANPRODUCT") && cnt.RIGHT_DETAIL_BTN,
    //   selectedRow[0]?.status?.toLowerCase() === "pending" && cnt.RIGHT_EDIT_BTN,
    // ].map((item) => {
    //   dispatch({
    //     type: item,
    //   });
    // });

    if (BOM.HasPermission("READ_INTERBANK_PLACEMENT_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }
    if (
      selectedRow[0]?.status?.toLowerCase() === "pending" &&
      BOM.HasPermission("UPDATE_INTERBANK_PLACEMENT_PRODUCT")
    ) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  function handleOnView() {
    const { detailBtn } = rightBtn;
    if (detailBtn.toLowerCase() === "primary") {
      navigate("/view-interbank-placement", { state: selectedRow });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  function handleOnCreate() {
    const { addBtn } = rightBtn;
    if (addBtn.toLowerCase() === "primary") {
      navigate("/add-interbank-placement", { ofObj: selectedRow });
    } else {
      console.log("Could not open the link");
    }
  }

  function handleOnEdit() {
    const { editBtn } = rightBtn;
    if (editBtn.toLowerCase() === "primary") {
      navigate("/edit-interbank-placement", { state: selectedRow });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.interBankPlacementItems}
    >
      <TopContent
        pageTitle={labelConfig.interBankPlacement}
        createPageTitle={labelConfig.productListing}
      />

      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        {/* RENDER TABLE LIST HERE*/}
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div>
              <Input
                onChange={handleSearchInTable}
                placeholder="Search"
                prefix={<SearchLg width={18} color="#667085" />}
                style={{
                  width: 320,
                  borderRadius: "10px",
                }}
              />
            </div>
            <div
              style={{
                margin: "0px 10px",
                display: "flex",
                alignItems: "center",
                // backgroundColor: "#F9FAFB",
                // borderRadius: 50,
                border: "none",
                // borderColor: "#EAECF0",
                padding: 2,
                width: "fit-content",
              }}
            >
              <Select
                defaultValue="Active"
                options={staticConfig.statusLabels}
                onSelect={handleFilterByStatus}
                bordered={false}
                style={{
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #EAECF0",
                  // backgroundColor: "transparent",
                  borderRadius: 50,
                  height: 30,

                  // minWidth: 120,
                }}
                dropdownStyle={{
                  borderRadius: 8,
                  width: "250px",
                }}
              />
            </div>

            <DownloadTableButtons
              downloadData={interBankPlacementList}
              columns={columns}
              fileName={"Interbank Placement List"}
              // onExportClick={getCsvListData}
            />
          </Row>
          <Table
            className="interBankPlacement-table"
            rowSelection={{
              onChange: onSelectItemAction,
              hideSelectAll: true,
              // selectedRowKeys: selectedRow ? [selectedRow.key] : [],
              // getCheckboxProps: (record) => ({
              //   disabled: selectedRow && record.key !== selectedRow.key,
              // }),
            }}
            columns={columns}
            dataSource={interBankData}
            pagination={false}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            {/* Empty space on the left */}
            <div>
              <Button
                style={{
                  backgroundColor: "white",
                  borderRadius: 8,
                  border: "1px solid #D0D5DD",
                  padding: "8px 12px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
                className="previous-button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ArrowLeftOutlined style={{ width: "20px", height: "20px" }} />{" "}
                Previous
              </Button>
            </div>

            {/* Centered Pagination */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <Pagination
                current={currentPage}
                total={filteredData.length}
                pageSize={pageSize}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
                itemRender={itemRender}
                align="center"
              />
            </div>

            {/* Next button on the right */}
            <div>
              <Button
                style={{
                  backgroundColor: "white",
                  borderRadius: 8,
                  border: "1px solid #D0D5DD",
                  padding: "8px 12px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
                className="previous-button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage >= totalPages}
              >
                Next <ArrowRight style={{ width: "20px", height: "20px" }} />
              </Button>
            </div>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageInterBankPlacement);
